import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  id: "google_oauth_form",
  class: "w-full"
}
const _hoisted_2 = { class: "field" }
const _hoisted_3 = { class: "field" }
const _hoisted_4 = { class: "field" }
const _hoisted_5 = { class: "field" }
const _hoisted_6 = { class: "field" }

import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import MultiSelect from 'primevue/multiselect';
import { useRouting } from '@/composables';
import { OAUTH_PROVIDER_SLUG } from '@/enum';
import { useTenantIntegrationStore } from '@/store';
import { OAuthFormIntegration } from '@/types/OAuth';


export default /*@__PURE__*/_defineComponent({
  __name: 'GoogleOAuth',
  setup(__props) {

const tenantIntegrationStore = useTenantIntegrationStore();

const { loading, error, roles, users } = storeToRefs(tenantIntegrationStore);

const $routing = useRouting();

const rules = {
  enforcedRoles: {},
  bypassUsers: {},
  clientId: { required },
  clientSecret: { required },
  redirectUrl: { required },
};

const payload = ref<OAuthFormIntegration>({
  enforcedRoles: [],
  bypassUsers: [],
  clientId: '',
  clientSecret: '',
  redirectUrl: '',
});

const v$ = useVuelidate(rules, payload);

const originalForm = ref<OAuthFormIntegration | null>(null);

const tenantId = ref(Number($routing.route.params.id));

const integration = ref();

const errors = ref(null);

const isLoading = computed(() => loading.value);

onMounted(async () => await init());

const init = async () => {
  integration.value = tenantIntegrationStore.findIntegration(OAUTH_PROVIDER_SLUG.GOOGLE);

  if (integration.value) {
    const integrationRoles = integration.value.integration_role;
    const enforcedRoles = integrationRoles
      .map((role) => roles.value.find((r) => r.id === role.role_id))
      .filter(Boolean);

    const bypassUsers = integration.value.integration_bypass_users
      .map((user) => users.value.find((u) => u.id === user.user_id))
      .filter(Boolean);

    payload.value = {
      id: integration.value.id,
      enforcedRoles: enforcedRoles,
      bypassUsers: bypassUsers,
      clientId: integration.value.data.client_id,
      clientSecret: integration.value.data.client_secret,
      redirectUrl: integration.value.data.redirect_url,
    };

    originalForm.value = { ...payload.value };
  }
};

const resetDirty = () => v$.value.$reset();

const fieldStyles = (key: string, styles?: string): string => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const onCancel = () => {
  if (originalForm.value) {
    payload.value = { ...originalForm.value };
  }
  errors.value = null;
  v$.value.$reset();
};

const onSave = async () => {
  if (v$.value.$invalid) return;

  const formData = new FormData();
  const configData = JSON.stringify({
    client_id: payload.value.clientId,
    client_secret: payload.value.clientSecret,
    redirect_url: payload.value.redirectUrl,
  });

  formData.append('data', configData);

  payload.value.enforcedRoles.forEach((role) => formData.append('integration_roles[]', String(role.id)));
  payload.value.bypassUsers.forEach((user) => formData.append('integration_bypass_users[]', String(user.id)));

  formData.append('integration_provider_type_slug', OAUTH_PROVIDER_SLUG.GOOGLE);

  payload.value.id
    ? await tenantIntegrationStore.updateTenantIntegration(tenantId.value, payload.value.id, formData)
    : await tenantIntegrationStore.saveTenantIntegration(tenantId.value, formData);

  if (!error.value) {
    await init();
    originalForm.value = { ...payload.value };
    resetDirty();
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Password = _resolveComponent("Password")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "Google OAuth",
    dirty: _unref(v$).$anyDirty,
    cancel: onCancel,
    save: onSave,
    "save-disabled": _unref(v$).$anyDirty && _unref(v$).$invalid
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[5] || (_cache[5] = _createElementVNode("label", { for: "enforced-roles" }, "Enforced Roles", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_unref(MultiSelect), {
                key: 1,
                id: "enforced-roles",
                modelValue: _unref(v$).enforcedRoles.$model,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).enforcedRoles.$model) = $event)),
                options: _unref(roles),
                "option-label": "title",
                placeholder: "Select Roles",
                display: "chip",
                class: "w-full",
                onBlur: _unref(v$).enforcedRoles.$touch
              }, null, 8, ["modelValue", "options", "onBlur"]))
        ]),
        _createElementVNode("div", _hoisted_3, [
          _cache[6] || (_cache[6] = _createElementVNode("label", { for: "bypass-users" }, "Users that can bypass Google OAuth", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_unref(MultiSelect), {
                key: 1,
                id: "bypass-users",
                modelValue: _unref(v$).bypassUsers.$model,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).bypassUsers.$model) = $event)),
                "data-key": "id",
                options: _unref(users),
                "option-label": "display_name",
                placeholder: "Select Users",
                display: "chip",
                filter: true,
                "show-toggle-all": false,
                class: "w-full",
                onBlur: _unref(v$).bypassUsers.$touch
              }, null, 8, ["modelValue", "options", "onBlur"]))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[7] || (_cache[7] = _createElementVNode("label", {
            for: "client-id",
            class: "required"
          }, "Google Client ID", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_Password, {
                key: 1,
                id: "client-id",
                modelValue: _unref(v$).clientId.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).clientId.$model) = $event)),
                "toggle-mask": true,
                class: _normalizeClass(fieldStyles('clientId', 'w-full')),
                placeholder: "Enter Google Client ID",
                feedback: false,
                autocomplete: "off"
              }, null, 8, ["modelValue", "class"]))
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[8] || (_cache[8] = _createElementVNode("label", {
            for: "client-secret",
            class: "required"
          }, "Google Client Secret", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_Password, {
                key: 1,
                id: "client-secret",
                modelValue: _unref(v$).clientSecret.$model,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).clientSecret.$model) = $event)),
                "toggle-mask": true,
                class: _normalizeClass(fieldStyles('clientSecret', 'w-full')),
                placeholder: "Enter Google Client Secret",
                feedback: false,
                autocomplete: "off"
              }, null, 8, ["modelValue", "class"]))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[9] || (_cache[9] = _createElementVNode("label", {
            for: "redirect-url",
            class: "required"
          }, "Google Redirect URL", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "redirect-url",
                modelValue: _unref(v$).redirectUrl.$model,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).redirectUrl.$model) = $event)),
                class: _normalizeClass(fieldStyles('redirectUrl', 'w-full')),
                placeholder: "Enter Google Redirect URL",
                autocomplete: "off"
              }, null, 8, ["modelValue", "class"]))
        ])
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})